import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Menu } from 'antd';
import { TbArrowsCross, TbExclamationCircle } from 'react-icons/tb';
import { Link } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useResourceAssociations } from '@/queries/tanstack/database/entities/resourceAssociations';
import { ResourceAssociationItem } from '@/services/database/resourceAssociations';
import { subscribeResourceCommentEvents } from '@/shared/components/activity/resourceCommentEvents';
import DropdownOptionContent from '@/shared/components/dropdowns/DropdownOptionContent';
import DropdownPanel, { DropdownMenuLabel, DropdownPanelMenu } from '@/shared/components/dropdowns/DropdownPanel';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import { EmptyView } from '@/shared/components/layouts/views/EmptySection';
import { ButtonSkeleton, ListItemSkeleton } from '@/shared/components/skeletons';
import { WorkspaceResourceGlyph } from '@/shared/components/workspace-resources/WorkspaceResourceAvatar';
import { ResourceActivityResourceType } from '@/types';

interface ResourceAssociationsProps {
    resourceId: string;
    resourceType: ResourceActivityResourceType;
    resourceUpdatedAt?: Date | string;
}

const resourceTypeOrder: ResourceActivityResourceType[] = [
    'investments',
    'lifecycles',
    'dashboards',
    'reports',
    'reportComponents',
    'lists',
    'listViews',
    'memos',
    'pipelines',
    'folders',
    'tasks',
    'teams',
];

const resourceTypeLabels: Partial<Record<ResourceActivityResourceType, string>> = {
    dashboards: 'Dashboards',
    folders: 'Folders',
    investments: 'Investments',
    lifecycles: 'Lifecycles',
    listViews: 'List Views',
    lists: 'Lists',
    memos: 'Memos',
    pipelines: 'Pipelines',
    reports: 'Reports',
    reportComponents: 'Report Components',
    tasks: 'Tasks',
    teams: 'Teams',
};

const associationLocationLabel = (item: ResourceAssociationItem) =>
    item.locations.map((location) =>
        location.mentionCount > 1 ? `${location.label} · ${location.mentionCount} mentions` : location.label,
    );

const groupAssociationItems = (items: ResourceAssociationItem[]) =>
    items.reduce<Partial<Record<ResourceActivityResourceType, ResourceAssociationItem[]>>>(
        (groups, item) => ({
            ...groups,
            [item.resource.resourceType]: [...(groups[item.resource.resourceType] ?? []), item],
        }),
        {},
    );

const ResourceAssociations: FC<ResourceAssociationsProps> = ({ resourceId, resourceType, resourceUpdatedAt }) => {
    const { token } = useAntDesignTheme();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const { data, isError, isLoading, refetch } = useResourceAssociations(resourceType, resourceId);
    const previousUpdatedAt = useRef(resourceUpdatedAt ? String(resourceUpdatedAt) : undefined);

    useEffect(
        () =>
            subscribeResourceCommentEvents(() => {
                void refetch();
            }),
        [refetch],
    );

    useEffect(() => {
        const nextUpdatedAt = resourceUpdatedAt ? String(resourceUpdatedAt) : undefined;

        if (previousUpdatedAt.current && nextUpdatedAt && previousUpdatedAt.current !== nextUpdatedAt) {
            void refetch();
        }

        previousUpdatedAt.current = nextUpdatedAt;
    }, [refetch, resourceUpdatedAt]);

    const filteredSections = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        const filterItems = (items: ResourceAssociationItem[]) =>
            !normalizedSearch
                ? items
                : items.filter((item) =>
                      [
                          item.resource.label,
                          resourceTypeLabels[item.resource.resourceType],
                          ...item.locations.map((location) => location.label),
                      ]
                          .filter(Boolean)
                          .some((value) => String(value).toLowerCase().includes(normalizedSearch)),
                  );

        return [
            { key: 'references', label: 'References', items: filterItems(data?.items ?? []) },
            { key: 'referenced-by', label: 'Referenced By', items: filterItems(data?.referencedBy ?? []) },
        ];
    }, [data?.items, data?.referencedBy, search]);
    const filteredItemCount = filteredSections.reduce((total, section) => total + section.items.length, 0);

    const panel = (
        <DropdownPanel
            footer={<LearnMoreFooter topic='resource associations and research lineage' variant='dropdown' />}
            maxHeight='22rem'
            search={{
                ariaLabel: 'Search associations',
                placeholder: 'Search Associations...',
                setValue: setSearch,
                value: search,
            }}
            width='24rem'
        >
            {isLoading ? (
                <div aria-label='Loading associations' style={{ display: 'grid', gap: '0.75rem', padding: '0.75rem' }}>
                    {Array.from({ length: 4 }).map((_item, index) => (
                        <ListItemSkeleton avatar avatarShape='square' key={index} />
                    ))}
                </div>
            ) : isError ? (
                <EmptyView
                    description='Associations could not be loaded. Your resource permissions and references remain unchanged.'
                    extra={
                        <Button size='small' type='link' onClick={() => void refetch()}>
                            Try Again
                        </Button>
                    }
                    height='11rem'
                    icon={<TbExclamationCircle size='1.25rem' />}
                    label='Unable to Load Associations'
                />
            ) : filteredItemCount ? (
                filteredSections.map((section) => {
                    if (!section.items.length) {
                        return null;
                    }

                    const groupedItems = groupAssociationItems(section.items);

                    return (
                        <div key={section.key}>
                            <DropdownMenuLabel icon={<TbArrowsCross size='0.875rem' />}>
                                {section.label}
                            </DropdownMenuLabel>
                            {resourceTypeOrder.map((groupResourceType) => {
                                const items = groupedItems[groupResourceType];

                                if (!items?.length) {
                                    return null;
                                }

                                return (
                                    <div key={`${section.key}:${groupResourceType}`}>
                                        <DropdownMenuLabel
                                            icon={
                                                <WorkspaceResourceGlyph resource={groupResourceType} size='0.875rem' />
                                            }
                                        >
                                            {resourceTypeLabels[groupResourceType] ?? groupResourceType}
                                        </DropdownMenuLabel>
                                        <DropdownPanelMenu selectable={false}>
                                            {items.map((item) => {
                                                const content = (
                                                    <DropdownOptionContent
                                                        avatar={{
                                                            icon: (
                                                                <WorkspaceResourceGlyph
                                                                    resource={item.resource.resourceType}
                                                                    size='0.875rem'
                                                                />
                                                            ),
                                                            website: item.resource.website,
                                                        }}
                                                        metadata={associationLocationLabel(item)}
                                                        singleLine
                                                        title={item.resource.label}
                                                    />
                                                );

                                                return (
                                                    <Menu.Item key={`${section.key}:${item.id}`}>
                                                        {item.resource.href ? (
                                                            <Link
                                                                to={item.resource.href}
                                                                style={{ color: 'inherit', display: 'block' }}
                                                            >
                                                                {content}
                                                            </Link>
                                                        ) : (
                                                            content
                                                        )}
                                                    </Menu.Item>
                                                );
                                            })}
                                        </DropdownPanelMenu>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })
            ) : (
                <EmptyView
                    description={
                        search
                            ? 'No associated workspace resources match your search.'
                            : 'Resources referenced here and resources that reference this item will appear in separate sections.'
                    }
                    height='11rem'
                    icon={<TbArrowsCross size='1.25rem' />}
                    label={search ? 'No Matching Associations' : 'No Associations'}
                />
            )}
        </DropdownPanel>
    );

    return (
        <Dropdown
            align={{ offset: [0, -22] }}
            destroyOnHidden
            open={open}
            popupRender={() => panel}
            placement='topLeft'
            trigger={['hover', 'click']}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                if (nextOpen) {
                    void refetch();
                }
                if (!nextOpen) {
                    setSearch('');
                }
            }}
        >
            <span
                aria-expanded={open}
                aria-haspopup='menu'
                aria-label={`${data?.total ?? 0} ${data?.total === 1 ? 'Association' : 'Associations'}`}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setOpen(true);
                    }
                    if (event.key === 'Escape') {
                        setOpen(false);
                    }
                }}
                role='button'
                tabIndex={0}
                style={{
                    alignItems: 'center',
                    color: token.colorTextTertiary,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                }}
            >
                <TbArrowsCross size='0.875rem' style={{ marginRight: '0.25rem' }} />
                {isLoading ? (
                    <span
                        aria-hidden='true'
                        style={{ alignItems: 'center', display: 'inline-flex', height: '0.875rem', lineHeight: 0 }}
                    >
                        <ButtonSkeleton
                            active
                            size='small'
                            style={{ height: '0.5rem', minWidth: '4.5rem', width: '4.5rem' }}
                        />
                    </span>
                ) : (
                    <small>
                        {isError
                            ? 'Associations'
                            : `${data?.total ?? 0} ${data?.total === 1 ? 'Association' : 'Associations'}`}
                    </small>
                )}
            </span>
        </Dropdown>
    );
};

export default ResourceAssociations;
